import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Bed, Bath, Square, SlidersHorizontal, X } from "lucide-react";
import { useApi } from "../context/ApiContext";

const PLACEHOLDER_IMAGE =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1200' height='800' viewBox='0 0 1200 800'%3E%3Cdefs%3E%3ClinearGradient id='g' x1='0' y1='0' x2='1' y2='1'%3E%3Cstop stop-color='%23f97316'/%3E%3Cstop offset='1' stop-color='%23fdba74'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='1200' height='800' fill='url(%23g)'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='white' font-family='Arial,sans-serif' font-size='56' font-weight='700'%3EProperty Preview%3C/text%3E%3C/svg%3E";

const Properties = () => {
  const navigate = useNavigate();
  const { fetchProperties } = useApi();

  const [selectedType, setSelectedType] = useState("All");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedLocation, setSelectedLocation] = useState("All");
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const [allProperties, setAllProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        setError("");
        const list = await fetchProperties();
        if (active) setAllProperties(Array.isArray(list) ? list : []);
      } catch (e) {
        if (active) setError(e?.message || "Failed to load properties");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [fetchProperties]);

  // Restrict locations to the four supported cities
  const locations = ["All", "Johannesburg", "Benoni", "Pretoria", "Witbank"];

  const filteredProperties = useMemo(() => {
    return (allProperties || []).filter((property) => {
      // Always hide properties that are "Sale"
      if (property.status === "Sale") return false;
      const matchesType = selectedType === "All" || property.status === selectedType;
      const matchesCategory = selectedCategory === "All" || property.category === selectedCategory;
      const matchesLocation = selectedLocation === "All" || property.location === selectedLocation;
      return matchesType && matchesCategory && matchesLocation;
    });
  }, [allProperties, selectedType, selectedCategory, selectedLocation]);

  const PropertyCard = ({ property }) => (
    <div
      onClick={() => navigate(`/properties/${encodeURIComponent(property.slug || property.id || "")}`)}
      className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-all cursor-pointer"
    >
      <div className="relative">
        <img
          src={property.image || property.heroImage || PLACEHOLDER_IMAGE}
          alt={property.name}
          className="w-full h-56 object-cover"
        />
        <div className="absolute top-3 left-3 flex gap-2">
          <span
            className={`text-xs font-semibold px-3 py-1 rounded-full ${
              property.category === "Residential"
                ? "bg-orange-500 text-white"
                : property.category === "Commercial"
                ? "bg-blue-500 text-white"
                : "bg-gray-700 text-white"
            }`}
          >
            {property.category}
          </span>
          <span className="bg-white/90 text-gray-900 text-xs font-semibold px-3 py-1 rounded-full">
            {property.status}
          </span>
        </div>
      </div>

      <div className="p-4">
        <div className="flex items-center text-gray-500 text-sm mb-2">
          <MapPin className="w-4 h-4 mr-1" />
          <span>{property.location}</span>
        </div>
        <h3 className="font-bold text-gray-900 mb-3 truncate">{property.name}</h3>
        {property.headline && (
          <p className="text-sm text-gray-600 line-clamp-2 mb-3">{property.headline}</p>
        )}

        {property.category === "Residential" ? (
          <div className="flex items-center text-gray-600 text-sm space-x-4 border-t pt-3">
            <div className="flex items-center">
              <Bed className="w-4 h-4 mr-1" />
              <span>{property.beds}</span>
            </div>
            <div className="flex items-center">
              <Bath className="w-4 h-4 mr-1" />
              <span>{property.baths}</span>
            </div>
            <div className="flex items-center">
              <Square className="w-4 h-4 mr-1" />
              <span>{property.sqft}</span>
            </div>
          </div>
        ) : (
          <div className="flex items-center text-gray-600 text-sm border-t pt-3">
            <Square className="w-4 h-4 mr-1" />
            <span>{property.sqft} sqft</span>
          </div>
        )}

        <div className="flex items-center justify-between mt-4 border-t pt-3">
          <span className="text-sm text-gray-700">
            {property.unitsAvailable > 0 ? `${property.unitsAvailable} units available` : property.agentName}
          </span>
          <span className="font-bold text-lg text-gray-900">
            {property.price != null && property.price !== "" ? `R${Number(property.price).toLocaleString()}` : ""}
          </span>
        </div>
      </div>
    </div>
  );

  const FilterSection = ({ isMobile = false }) => (
    <div className={`${isMobile ? "p-6" : "bg-white rounded-xl p-6 shadow-sm"}`}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-900">Filters</h2>
        {isMobile && (
          <button onClick={() => setShowMobileFilters(false)} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Category Filter */}
      <div className="mb-6">
        <h3 className="font-semibold text-gray-900 mb-3">Category</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-2 gap-2">
          {["All", "Residential", "Commercial", "Industrial"].map((category) => (
            <button
              key={category}
              onClick={() => {
                setSelectedCategory(category);
                if (isMobile) setShowMobileFilters(false);
              }}
              className={`px-4 py-3 text-sm rounded-lg font-medium transition-all ${
                selectedCategory === category ? "bg-orange-500 text-white shadow-md" : "bg-gray-50 text-gray-700 hover:bg-gray-100"
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* Location Filter */}
      <div className="mb-6">
        <h3 className="font-semibold text-gray-900 mb-3">Location</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-2 gap-2">
          {locations.map((loc) => (
            <button
              key={loc}
              onClick={() => {
                setSelectedLocation(loc);
                if (isMobile) setShowMobileFilters(false);
              }}
              className={`px-4 py-3 text-sm rounded-lg transition-all ${
                selectedLocation === loc ? "bg-gray-900 text-white shadow-md" : "bg-gray-50 text-gray-700 hover:bg-gray-100"
              }`}
            >
              {loc}
            </button>
          ))}
        </div>
      </div>

      {/* Type Filter */}
      <div>
        <h3 className="font-semibold text-gray-900 mb-3">Type</h3>
        <div className="flex gap-2">
          {["All", "Rent"].map((type) => (
            <button
              key={type}
              onClick={() => {
                setSelectedType(type);
                if (isMobile) setShowMobileFilters(false);
              }}
              className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                selectedType === type ? "bg-orange-500 text-white shadow-md" : "bg-gray-50 text-gray-700 hover:bg-gray-100"
              }`}
            >
              {type}
            </button>
          ))}
        </div>
      </div>

      {isMobile && (
        <button
          onClick={() => setShowMobileFilters(false)}
          className="w-full mt-6 px-6 py-3 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition"
        >
          Show {filteredProperties.length} Results
        </button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 pt-20 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header and Quick Filters */}
        <div className="mb-8">
          <span className="text-xs sm:text-sm text-orange-600 uppercase font-semibold">LATEST PROPERTIES</span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mt-2">Explore our properties</h1>

        {/* Mobile Quick Filters Bar */}
          <div className="flex items-center gap-3 mt-4 overflow-x-auto pb-2 hide-scrollbar">
            <button
              onClick={() => setShowMobileFilters(true)}
              className="flex-shrink-0 lg:hidden flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:border-gray-400 transition"
            >
              <SlidersHorizontal className="w-4 h-4" />
              Filters
            </button>

            {["All", "Rent"].map((type) => (
              <button
                key={type}
                onClick={() => setSelectedType(type)}
                className={`flex-shrink-0 px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                  selectedType === type
                    ? "bg-orange-500 text-white shadow-md"
                    : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"
                }`}
              >
                {type}
              </button>
            ))}

            <button
              onClick={() => setShowMobileFilters(true)}
              className="flex-shrink-0 px-4 py-2 text-sm font-medium rounded-lg transition-all bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"
            >
              Location: {selectedLocation === "All" ? "Any" : selectedLocation}
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-4 gap-6 lg:gap-8">
          {/* Sidebar Filters */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="sticky top-24">
              <FilterSection />
            </div>
          </div>

          {/* Property Grid */}
          <div className="lg:col-span-3">
            {loading ? (
              <div className="text-gray-600">Loading properties...</div>
            ) : error ? (
              <div className="text-red-600">{error}</div>
            ) : filteredProperties.length > 0 ? (
              <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
                {filteredProperties.map((property) => (
                  <PropertyCard key={property.slug || property.id} property={property} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-white rounded-xl">
                <h3 className="text-xl font-semibold text-gray-600 mb-2">No properties found</h3>
                <p className="text-gray-500 mb-4">Try adjusting your filters to see more results.</p>
                <button
                  onClick={() => {
                    setSelectedType("All");
                    setSelectedCategory("All");
                    setSelectedLocation("All");
                  }}
                  className="px-6 py-2 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Filter Modal */}
      {showMobileFilters && (
        <>
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden" onClick={() => setShowMobileFilters(false)} />
          <div className="fixed inset-x-0 bottom-0 top-20 bg-white z-40 lg:hidden overflow-y-auto rounded-t-3xl shadow-2xl animate-slideUp">
            <FilterSection isMobile={true} />
          </div>
        </>
      )}

      <style jsx>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        .animate-slideUp { animation: slideUp 0.3s ease-out forwards; }
      `}</style>
    </div>
  );
};

export default Properties;
